import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useLang } from "@/i18n";
import { API_BASE } from "@/lib/api";

const loadScript = (domain) =>
  new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI && window.__jitsiDomain === domain) return resolve();
    const s = document.createElement("script");
    s.src = `https://${domain}/external_api.js`;
    s.async = true;
    s.onload = () => {
      window.__jitsiDomain = domain;
      resolve();
    };
    s.onerror = () => reject(new Error("jitsi_script_failed"));
    document.body.appendChild(s);
  });

export function JitsiFrame({ domain, roomName, jwt, displayName, email, subject, lobby = false, onLeave, onApi }) {
  const ref = useRef(null);
  const apiRef = useRef(null);
  const leftRef = useRef(false);
  const [state, setState] = useState("loading");
  const { lang, t } = useLang();

  useEffect(() => {
    if (!domain || !roomName || !ref.current) return undefined;
    let cancelled = false;
    leftRef.current = false;
    const leave = () => {
      if (leftRef.current) return;
      leftRef.current = true;
      onLeave?.();
    };
    loadScript(domain)
      .then(() => {
        if (cancelled || !ref.current) return;
        const jitsi = new window.JitsiMeetExternalAPI(domain, {
          roomName,
          jwt: jwt || undefined,
          parentNode: ref.current,
          width: "100%",
          height: "100%",
          lang,
          userInfo: { displayName, email },
          configOverwrite: {
            prejoinConfig: { enabled: true },
            disableDeepLinking: true,
            defaultLanguage: lang,
            enableWelcomePage: false,
            enableClosePage: false,
            disableThirdPartyRequests: true,
            feedbackPercentage: 0,
            disableInviteFunctions: true,
            hideConferenceSubject: false,
            subject: subject || undefined,
            dynamicBrandingUrl: `${API_BASE}/public/branding`,
            defaultLogoUrl: `${window.location.origin}/televox-logo.svg`,
            toolbarButtons: [
              "microphone", "camera", "desktop", "chat", "raisehand", "participants-pane", "tileview",
              "settings", "fullscreen", "hangup", "select-background", "noisesuppression", "whiteboard",
            ],
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            SHOW_BRAND_WATERMARK: false,
            SHOW_POWERED_BY: false,
            SHOW_PROMOTIONAL_CLOSE_PAGE: false,
            DEFAULT_LOGO_URL: `${window.location.origin}/televox-logo.svg`,
            DEFAULT_WELCOME_PAGE_LOGO_URL: `${window.location.origin}/televox-logo.svg`,
            JITSI_WATERMARK_LINK: window.location.origin,
            DEFAULT_BACKGROUND: "#06121A",
            DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
            MOBILE_APP_PROMO: false,
          },
        });
        apiRef.current = jitsi;
        jitsi.addListener("videoConferenceJoined", () => {
          setState("ready");
          if (subject) jitsi.executeCommand("subject", subject);
          if (lobby) setTimeout(() => jitsi.executeCommand("toggleLobby", true), 1500);
        });
        jitsi.addListener("videoConferenceLeft", leave);
        jitsi.addListener("readyToClose", leave);
        onApi?.(jitsi);
        setState("ready");
      })
      .catch(() => !cancelled && setState("error"));
    return () => {
      cancelled = true;
      try {
        apiRef.current?.dispose();
      } catch {
        /* noop */
      }
      apiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, roomName, jwt]);

  return (
    <div className="relative h-full w-full bg-vox-deep">
      <div ref={ref} className="h-full w-full" data-testid="jitsi-meeting-iframe" />
      {state === "loading" && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-vox-muted">
          <div className="flex items-center gap-3 text-sm"><Loader2 className="animate-spin" size={16} />{t("room.loadingVideo")}</div>
        </div>
      )}
      {state === "error" && (
        <div className="absolute inset-0 grid place-items-center text-sm text-[#ff9b9e]" data-testid="jitsi-error">{t("room.videoError")} ({domain})</div>
      )}
    </div>
  );
}
